import { InspectionReportDownload } from '@components/Shared/Inspection/InspectionReportDownload';
import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { Box, Tooltip, Typography } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { Calendar, Images, User } from 'lucide-react';
import { memo, useMemo } from 'react';
import {
  InfoChip, InspectionHeader, InspectionInfo, InspectionRowBody, InspectionRowCard, SnapshotChip,
} from './Inspections.styled';
import InspectionStatusSummary from './InspectionStatusSummary';

interface Props {
  inspection: IInspection;
  projectId: string;
  projectName: string;
  onOpen: (inspectionId: string) => void;
}

/**
 * One inspection in the list: what it is, how it went, and a way to its report.
 *
 * Everything that needs the inspection's file — its points of interest, its snapshots —
 * lives on the inspection's own page. The counts shown here come from the listing itself,
 * so a list of fifty rows downloads nothing.
 */
const InspectionRow = memo<Props>(({ inspection, projectId, projectName, onOpen }) => {
  const { t } = useLocalization();

  const pois = inspection.pointOfInterests ?? [];

  const snapshotCount = useMemo(
    () => (inspection.resources ?? []).filter(SPInspection.isSnapshot).length,
    [inspection.resources],
  );

  return (
    <InspectionRowCard onClick={() => onOpen(inspection.info.id)}>
      {/* The inner spacing an AccordionSummary provides, which is what sits inside a
          checklist row: 16px at the sides, 12px above and below its content, never under
          48px tall. The card contributes 4 around this, as the accordion does. */}
      <InspectionRowBody>
        <InspectionHeader sx={{ flexGrow: 1, minWidth: 0, py: 1.5, minHeight: 48 }}>
          <InspectionInfo sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {inspection.info.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {inspection.info.id}
            </Typography>
          </InspectionInfo>

          {/* One line, never two: wrapped chips add their own height to the row, and the
              checklist row beside this one carries a single count that cannot wrap. The
              name to the left is what gives way instead — it truncates. */}
          <Box sx={{
            display: 'flex', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end',
            flexShrink: 0, gap: 1, marginRight: 1.5
          }}>
            <InspectionStatusSummary pois={pois} />

            {snapshotCount > 0 && (
              <Tooltip title={t('Common_Snapshots')}>
                <SnapshotChip
                  icon={<Images size={14} />}
                  label={snapshotCount}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
            )}

            {inspection.author && (
              <Tooltip title={t('Inspection_UploadedBy')}>
                <InfoChip
                  icon={<User size={14} style={{ marginRight: '1px' }} />}
                  label={inspection.author.name}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            )}
            <Tooltip title={t('Common_DateCreated')}>
              <InfoChip
                icon={<Calendar size={14} style={{ marginRight: '1px' }} />}
                label={new Date(inspection.info.dateCreated).toAgoString()}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </Box>
        </InspectionHeader>

        {/* Downloading is its own action: the click must not also open the row. */}
        <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(event) => event.stopPropagation()}>
          <InspectionReportDownload inspection={inspection} projectId={projectId} projectName={projectName} />
        </Box>
      </InspectionRowBody>
    </InspectionRowCard>
  );
});

export default InspectionRow;
